import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<Movie | undefined>(undefined);
  const [relatedMovies, setRelatedMovies] = useState<Movie[]>([]);
  const [activeEpisode, setActiveEpisode] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      const found = getMovieById(id);
      if (found) {
        setMovie(found);
        setRelatedMovies(getRelatedMovies(found, 6));
        setActiveEpisode(1);
      }
    }
  }, [id]);

  if (!movie) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 text-lg">未找到该电视剧</p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回电视剧大全
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '电视剧大全', path: '/category/all' },
    { label: movie.category, path: `/category/${movie.categorySlug}` },
    { label: movie.title },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.image,
    url: `https://www.muguays.com/detail/${movie.id}`,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(movie.views / 1000),
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <main className="pt-16">
        <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={movie.bannerImage}
            alt={`${movie.title} - 高清电视剧在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-32 relative z-10">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex flex-col md:flex-row gap-6 mt-6">
            <div className="shrink-0 w-[200px] md:w-[240px]">
              <img
                src={movie.image}
                alt={movie.title}
                title={`${movie.title} - 木瓜影视高清全集`}
                className="w-full rounded-lg shadow-sm"
              />
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                {movie.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">{movie.engTitle}</p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">
                  {movie.rating} 分
                </span>
                <span className="text-sm text-foreground-600">{movie.year}</span>
                <span className="text-foreground-400">·</span>
                <span className="text-sm text-foreground-600">{movie.region}</span>
                <span className="text-foreground-400">·</span>
                <span className="text-sm text-foreground-600">{movie.category}</span>
                <span className="text-foreground-400">·</span>
                <span className="text-sm text-secondary-600 font-medium">
                  共{movie.episodeCount}集 · {movie.status}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="mt-4 text-sm text-foreground-700 leading-relaxed max-w-2xl">
                {movie.longDescription}
              </p>

              <div className="mt-4 text-sm text-foreground-600 space-y-1">
                <p>
                  <strong className="text-foreground-800">导演：</strong>
                  {movie.director}
                </p>
                <p>
                  <strong className="text-foreground-800">主演：</strong>
                  {movie.cast.join(' / ')}
                </p>
                <p>
                  <strong className="text-foreground-800">更新：</strong>
                  更新至{movie.episodeUpdated}集 / 共{movie.episodeCount}集
                </p>
                <p>
                  <strong className="text-foreground-800">播放量：</strong>
                  {(movie.views / 10000).toFixed(0)}万次
                </p>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer">
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即播放
                </button>
                <button className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
                <button className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-lg font-heading text-foreground-900 mb-4">剧集列表</h2>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: movie.episodeUpdated }, (_, i) => i + 1).map((ep) => (
                <button
                  key={ep}
                  onClick={() => setActiveEpisode(ep)}
                  className={`whitespace-nowrap w-12 h-9 flex items-center justify-center text-sm rounded-md transition-all cursor-pointer ${
                    activeEpisode === ep
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {ep}
                </button>
              ))}
            </div>
          </div>

          {relatedMovies.length > 0 && (
            <div className="mt-10 mb-8">
              <h2 className="text-lg font-heading text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}